// v50.300 — Global daily sign-off chase banner.
//
// Lives in the app shell (ProtectedShell), below the top bar, on
// EVERY route. Per the Dashboard Remodel 08-06 brief Part 2: this is
// what makes the daily sign-off a *chase* rather than a dashboard
// widget the user can navigate away from.
//
// Behaviour:
//   • Appears on sign-in when !complete, persists across navigation.
//   • Minimisable to a slim floating pill — but re-expands on every
//     route navigation. There is no "dismiss for the day".
//   • Urgency escalates calm → amber → red as windowClosesAt nears:
//       calm    (>8h)  — neutral blue strip, minimisable
//       warning (2-8h) — amber, slightly more prominent
//       critical (<2h) — red, forced expanded (cannot stay minimised)
//   • Completion: when outstandingCount hits 0, the banner is
//     replaced by a brief calm green confirmation, auto-dismisses
//     after a few seconds.
//   • Daily reset: the chase is per working day. New day → re-eval
//     (the hook re-derives, so this is automatic on the next load).
//   • Respects deemed-confirm: when items auto-confirm the
//     outstandingCount drops, banner stops chasing.
//
// Non-modal, never traps the user, never scolds.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { useDailySignOff, formatPounds, formatTimeLeft } from "./useDailySignOff";
/* v50.317 — Stage 3 of the Dashboard Remodel brief: browser
   notifications at warning/critical thresholds + optional 1h snooze
   ("still returns"). Both managed by the new notifications hook. */
import { useDailySignOffNotifications } from "./useDailySignOffNotifications";

const SIGN_OFF_HREF = "/projects/site-ops?tab=exceptions";

export function GlobalSignOffBanner() {
  const state = useDailySignOff();
  const notif = useDailySignOffNotifications(state);
  const location = useLocation();

  /* Per the brief: "resets to 'expanded' on each route change". */
  const [minimised, setMinimised] = useState(false);
  useEffect(() => { setMinimised(false); }, [location.pathname]);

  /* Critical urgency forces expanded — cannot stay minimised. */
  const forcedExpanded = state.urgency === "critical";
  const isExpanded = forcedExpanded || !minimised;

  /* Completion confirmation: when outstandingCount transitions from
     >0 → 0 within this session, show a brief green strip then hide. */
  const wasOutstanding = useRef(false);
  const [showCompletion, setShowCompletion] = useState(false);
  useEffect(() => {
    if (!state.ready) return;
    if (state.outstandingCount > 0) {
      wasOutstanding.current = true;
      return;
    }
    /* outstandingCount === 0 */
    if (wasOutstanding.current) {
      setShowCompletion(true);
      wasOutstanding.current = false;
      const t = window.setTimeout(() => setShowCompletion(false), 6000);
      return () => window.clearTimeout(t);
    }
  }, [state.ready, state.outstandingCount]);

  /* Don't render anything until we know — avoids a flash of chase
     for a user who's already signed off everything. */
  if (!state.ready) return null;

  /* v50.317 — snooze suppresses the banner entirely. Still returns
     once the snooze period elapses (the notif hook ticks state and
     forces a re-render). */
  if (notif.snoozed) return null;

  /* Completion state — brief green strip then nothing. */
  if (state.complete) {
    if (!showCompletion) return null;
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full border-b border-green-200 bg-lc-green-bg/60 px-4 py-2 flex items-center gap-3"
      >
        <span className="w-6 h-6 rounded-full bg-lc-green-fg text-white flex items-center justify-center shrink-0">
          <Icon name="check" size={13} />
        </span>
        <div className="text-[12px] font-extrabold text-lc-green-fg">
          All daily sign-offs complete · revenue chain healthy
        </div>
      </div>
    );
  }

  /* v50.305 — banner tones per labourcore-dashboard-final mockup:
     calm = blue-grey #E8EEF5 (not blue-light — more neutral),
     warn = amber-tint, crit = red-tint with inset 2px red border. */
  const tone = state.urgency === "critical"
    ? { bg: "bg-[#FBEAEA]",   border: "border-[#D7373B]",   text: "text-[#D7373B]",   accent: "bg-[#D7373B]", chip: "bg-[#F3C6C6] text-[#D7373B] font-extrabold" }
    : state.urgency === "warning"
    ? { bg: "bg-[#FBF0DD]",   border: "border-[#F2D9A8]",   text: "text-[#8a5300]",   accent: "bg-[#C77700]", chip: "bg-[#F2D9A8] text-[#8a5300]" }
    : { bg: "bg-[#E8EEF5]",   border: "border-[#D5E0EE]",   text: "text-[#475569]",   accent: "bg-[#D5E0EE] text-[#475569]", chip: "bg-[#D5E0EE] text-[#475569]" };

  const verb = state.role === "coordinator" ? "to confirm" : "to submit";
  const noun = state.outstandingCount === 1 ? "daily log" : "daily logs";

  if (!isExpanded) {
    /* v50.305 — minimised pill per labourcore-dashboard-final .minpill
       spec: dark navy floating pill top-right, just below the topbar.
       One tap to re-expand. */
    return (
      <button
        type="button"
        onClick={() => setMinimised(false)}
        className="fixed top-[68px] right-6 z-[900] rounded-full shadow-lg bg-[#1d293b] text-white px-3.5 py-2 text-[12.5px] font-bold flex items-center gap-2 hover:scale-[1.02] transition-transform"
        aria-label="Expand daily sign-off chase"
      >
        <Icon name="clock" size={13} />
        {state.outstandingCount} {state.role === "coordinator" ? "logs" : "to submit"} · {formatTimeLeft(state.windowClosesAt)}
        <span aria-hidden>›</span>
      </button>
    );
  }

  /* v50.305 — banner chrome per mockup .gbanner spec: 11px / 30px
     padding (vs current 4/10), 30×30 rounded-square icon block, count
     chip in chip-tone, solid CTA button in tone-coloured fill (not
     just a text link). Critical adds inset 2px red border. */
  const criticalBorder = state.urgency === "critical" ? "shadow-[inset_0_0_0_2px_#D7373B]" : "";
  const ctaButton = state.urgency === "critical"
    ? "bg-[#D7373B] text-white font-extrabold"
    : state.urgency === "warning"
    ? "bg-[#C77700] text-white font-semibold"
    : "bg-white border border-lc-blue text-lc-blue font-semibold";

  return (
    <div
      role="status"
      aria-live={state.urgency === "critical" ? "assertive" : "polite"}
      className={`w-full border-b border-[rgba(60,60,67,0.13)] ${tone.bg} ${criticalBorder} px-7 py-2.5 flex items-center gap-3.5 flex-wrap`}
    >
      <span className={`w-[30px] h-[30px] rounded-lg ${tone.accent} flex items-center justify-center shrink-0`}>
        <Icon name="clock" size={15} />
      </span>
      <div className={`flex-1 min-w-0 text-[13.5px] ${tone.text}`}>
        <span className="font-semibold">{state.outstandingCount} {noun} {verb}</span>
        {state.moneyUnlocked > 0 && <> · <span className="font-bold">releases {formatPounds(state.moneyUnlocked)}</span></>}
      </div>
      <span className={`text-[12px] font-bold px-3 py-1 rounded-full ${tone.chip} ${state.urgency === "critical" ? "animate-pulse" : ""}`}>
        Closes in {formatTimeLeft(state.windowClosesAt)}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {/* v50.317 — permission-prompt CTA. Surfaces only when the
            browser hasn't been asked yet and the urgency is at
            warning+. Self-hides once granted/denied. */}
        {(notif.permission === "default") && (state.urgency !== "calm") && (
          <button
            type="button"
            onClick={() => void notif.requestPermission()}
            className="text-[12px] px-2.5 py-1.5 rounded-[9px] bg-white/70 border border-[rgba(60,60,67,0.13)] text-lc-ink-2 font-semibold hover:bg-white shrink-0"
            title="Get a browser notification when the review window closes in <2h"
          >
            Enable reminders
          </button>
        )}
        <Link
          to={SIGN_OFF_HREF}
          className={`text-[13px] px-3.5 py-2 rounded-[9px] ${ctaButton} hover:opacity-90 transition-opacity shrink-0`}
        >
          Sign off now
        </Link>
        {/* v50.317 — snooze button. Brief guardrail: unavailable at
            critical urgency. Hides the banner for 1h and lets it
            return — defer, don't dismiss. */}
        {!forcedExpanded && (
          <button
            type="button"
            onClick={notif.snoozeFor1h}
            className="text-[12px] px-2.5 py-1.5 rounded-[9px] bg-white/60 border border-[rgba(0,0,0,0.12)] text-lc-mute font-semibold hover:bg-white shrink-0"
            aria-label="Snooze for 1 hour"
            title="Hide for 1h — returns automatically"
          >
            Snooze 1h
          </button>
        )}
        {!forcedExpanded && (
          <button
            type="button"
            onClick={() => setMinimised(true)}
            className="w-[30px] h-[30px] rounded-lg border-[0.5px] border-[rgba(0,0,0,0.12)] bg-white/60 text-[15px] font-bold text-lc-mute hover:bg-white shrink-0"
            aria-label="Minimise the chase banner"
            title="Re-expands on the next page navigation"
          >
            −
          </button>
        )}
      </div>
    </div>
  );
}
